import React, { useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from '@apollo/client';
import { Select, InputNumber, Switch, Input, Button, Tooltip } from 'antd';
import { GET_ALL_EMAIL_TEMPLATES } from '../../../../Queries/emailTemplates';
import {
  PlusOutlined, DeleteOutlined, ThunderboltOutlined,
  ExperimentOutlined, ClockCircleOutlined, AimOutlined,
  NumberOutlined,
} from '@ant-design/icons';

const { Option } = Select;
const { TextArea } = Input;

const COLORS = {
  primary: '#1a1a2e',
  surface: '#1e1e3a',
  surfaceLight: '#252547',
  border: 'rgba(255,255,255,0.08)',
  borderLight: 'rgba(255,255,255,0.12)',
  text: '#e2e8f0',
  textSecondary: '#94a3b8',
  textMuted: '#64748b',
  emerald: '#10b981',
  emeraldBg: 'rgba(16,185,129,0.12)',
  amber: '#f59e0b',
  amberBg: 'rgba(245,158,11,0.12)',
  rose: '#ef4444',
  roseBg: 'rgba(239,68,68,0.12)',
  violet: '#8b5cf6',
  violetBg: 'rgba(139,92,246,0.12)',
  blue: '#3b82f6',
  blueBg: 'rgba(59,130,246,0.12)',
  glass: 'rgba(255,255,255,0.04)',
  glassBorder: 'rgba(255,255,255,0.08)',
};

const STEP_COLORS = ['#3b82f6', '#f59e0b', '#10b981', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4'];

const VARIABLES = [
  { key: 'agencyName', label: 'Agency Name' },
  { key: 'contactFirstName', label: 'First Name' },
  { key: 'contactTitle', label: 'Title' },
  { key: 'city', label: 'City' },
  { key: 'state', label: 'State' },
  { key: 'senderFirstName', label: 'Sender First Name' },
  { key: 'senderFullName', label: 'Sender Full Name' },
];

const TARGETING_OPTIONS = [
  { value: 'all', label: 'All Recipients' },
  { value: 'non_repliers', label: 'Non-Repliers' },
  { value: 'openers', label: 'Openers Only' },
  { value: 'non_openers', label: 'Non-Openers' },
  { value: 'clicked', label: 'Clicked Only' },
];

const AUTO_FOLLOWUP_TRIGGERS = [
  { value: 'opened', label: 'Opened Email' },
  { value: 'clicked', label: 'Clicked Link' },
  { value: 'no_reply_48h', label: 'No Reply (48h)' },
  { value: 'no_open_24h', label: 'Not Opened (24h)' },
  { value: 'bounced', label: 'Bounced' },
];

const glassCard = {
  background: COLORS.glass,
  backdropFilter: 'blur(16px)',
  WebkitBackdropFilter: 'blur(16px)',
  border: `1px solid ${COLORS.glassBorder}`,
  borderRadius: 12,
};

const variableChipStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 10px',
  borderRadius: 6,
  fontSize: 11,
  fontWeight: 600,
  color: COLORS.violet,
  background: COLORS.violetBg,
  border: `1px solid rgba(139,92,246,0.2)`,
  cursor: 'pointer',
  transition: 'all 0.15s',
  whiteSpace: 'nowrap',
};

const sectionLabelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: COLORS.textSecondary,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  marginBottom: 6,
};

const StepBuilder = ({ steps, onStepsChange, subjectLine, onSubjectChange }) => {
  const subjectRef = useRef(null);
  const loginUser = useSelector(state => state?.loginUserInfo?.loginUserDetails);
  const { data: templatesData } = useQuery(GET_ALL_EMAIL_TEMPLATES, {
    variables: { employeeId: loginUser?._id || loginUser?.id || '' },
    skip: !loginUser?._id && !loginUser?.id,
    fetchPolicy: 'network-only',
  });
  const templates = templatesData?.getAllEmailTemplates?.response || [];

  const handleStepChange = useCallback((index, field, value) => {
    const updated = steps.map((step, i) => (i === index ? { ...step, [field]: value } : step));
    onStepsChange(updated);
  }, [steps, onStepsChange]);

  const handleAddStep = useCallback(() => {
    const newStep = {
      stepNumber: steps.length + 1,
      templateId: null,
      templateName: '',
      delayDays: 2,
      delayHours: 0,
      targeting: 'non_repliers',
      abTest: false,
      variantA: { subject: '', body: '' },
      variantB: { subject: '', body: '' },
      autoFollowUp: false,
      autoFollowUpTrigger: 'no_reply_48h',
      autoFollowUpDelayHours: 24,
    };
    onStepsChange([...steps, newStep]);
  }, [steps, onStepsChange]);

  const handleRemoveStep = useCallback((index) => {
    if (index === 0) return;
    const updated = steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, stepNumber: i + 1 }));
    onStepsChange(updated);
  }, [steps, onStepsChange]);

  const insertVariable = useCallback((variableKey) => {
    const tag = `{{${variableKey}}}`;
    // Always append to subject line — using ref to get cursor position if available
    const el = subjectRef.current?.input || subjectRef.current;
    if (el && onSubjectChange) {
      const start = el.selectionStart ?? (subjectLine || '').length;
      const end = el.selectionEnd ?? start;
      const newVal = (subjectLine || '').substring(0, start) + tag + (subjectLine || '').substring(end);
      onSubjectChange(newVal);
      // Restore cursor after React re-render
      setTimeout(() => {
        if (el) {
          el.focus();
          el.setSelectionRange(start + tag.length, start + tag.length);
        }
      }, 0);
    } else if (onSubjectChange) {
      onSubjectChange((subjectLine || '') + tag);
    }
  }, [subjectLine, onSubjectChange]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Subject Line */}
      <div style={{ ...glassCard, padding: 16 }}>
        <div style={sectionLabelStyle}>Campaign Subject Line</div>
        <Input
          ref={subjectRef}
          value={subjectLine}
          onChange={(e) => onSubjectChange && onSubjectChange(e.target.value)}
          placeholder="Enter subject line... Use {{variables}} for personalization"
          style={{ marginBottom: 10 }}
          size="large"
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: COLORS.textMuted, marginRight: 4 }}>Insert:</span>
          {VARIABLES.map((v) => (
            <span
              key={v.key}
              style={variableChipStyle}
              onClick={() => insertVariable(v.key)}
              onMouseEnter={(e) => { e.target.style.background = 'rgba(139,92,246,0.25)'; }}
              onMouseLeave={(e) => { e.target.style.background = COLORS.violetBg; }}
            >
              {`{{${v.key}}}`}
            </span>
          ))}
        </div>
      </div>

      {/* Step Cards */}
      {steps.map((step, index) => {
        const stepColor = STEP_COLORS[index % STEP_COLORS.length];
        const stepColorBg = `${stepColor}18`;

        return (
          <div
            key={index}
            style={{
              ...glassCard,
              padding: 0,
              overflow: 'hidden',
              borderLeft: `3px solid ${stepColor}`,
            }}
          >
            {/* Step Header */}
            <div style={{
              padding: '10px 16px',
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              background: stepColorBg,
              borderBottom: `1px solid ${COLORS.border}`,
            }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 28,
                height: 28,
                borderRadius: 8,
                background: stepColor,
                color: '#fff',
                fontSize: 13,
                fontWeight: 700,
              }}>
                {step.stepNumber}
              </span>
              <span style={{ fontWeight: 600, color: COLORS.text, fontSize: 14, flex: 1 }}>
                Step {step.stepNumber}
                {index === 0 && (
                  <span style={{ fontSize: 11, color: COLORS.textMuted, fontWeight: 400, marginLeft: 8 }}>
                    (Initial Email)
                  </span>
                )}
              </span>
              {index > 0 && (
                <Tooltip title="Remove Step">
                  <Button
                    type="text"
                    danger
                    icon={<DeleteOutlined />}
                    size="small"
                    onClick={() => handleRemoveStep(index)}
                    style={{ color: COLORS.rose }}
                  />
                </Tooltip>
              )}
            </div>

            {/* Step Body */}
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Row 1: Template + Delay + Targeting */}
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                {/* Template */}
                <div style={{ flex: 2, minWidth: 200 }}>
                  <div style={sectionLabelStyle}>
                    <NumberOutlined style={{ marginRight: 4 }} />
                    Email Template
                  </div>
                  <Select
                    value={step.templateId}
                    onChange={(val) => {
                      const selected = templates.find(t => t.id === val);
                      const updated = steps.map((s, i) => i === index ? { ...s, templateId: val, templateName: selected?.name || '' } : s);
                      onStepsChange(updated);
                    }}
                    placeholder="Select template..."
                    style={{ width: '100%' }}
                    allowClear
                    showSearch
                    filterOption={(input, option) => (option?.children || '').toLowerCase().includes(input.toLowerCase())}
                    dropdownStyle={{ background: '#fff' }}
                  >
                    {templates.map(t => (
                      <Option key={t.id} value={t.id}>
                        {t.name}{t.category ? ` (${t.category})` : ''}
                      </Option>
                    ))}
                  </Select>
                </div>

                {/* Delay */}
                <div style={{ minWidth: 140 }}>
                  <div style={sectionLabelStyle}>
                    <ClockCircleOutlined style={{ marginRight: 4 }} />
                    Delay
                  </div>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                    <InputNumber
                      min={0}
                      max={90}
                      value={step.delayDays}
                      onChange={(val) => handleStepChange(index, 'delayDays', val || 0)}
                      size="middle"
                      style={{ width: 65 }}
                      disabled={index === 0}
                    />
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>days</span>
                    <InputNumber
                      min={0}
                      max={23}
                      value={step.delayHours}
                      onChange={(val) => handleStepChange(index, 'delayHours', val || 0)}
                      size="middle"
                      style={{ width: 65 }}
                      disabled={index === 0}
                    />
                    <span style={{ fontSize: 11, color: COLORS.textMuted }}>hrs</span>
                  </div>
                </div>

                {/* Targeting */}
                <div style={{ flex: 1, minWidth: 160 }}>
                  <div style={sectionLabelStyle}>
                    <AimOutlined style={{ marginRight: 4 }} />
                    Targeting
                  </div>
                  <Select
                    value={index === 0 ? 'all' : step.targeting}
                    onChange={(val) => handleStepChange(index, 'targeting', val)}
                    style={{ width: '100%' }}
                    disabled={index === 0}
                    dropdownStyle={{ background: '#fff' }}
                  >
                    {TARGETING_OPTIONS.map((opt) => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                </div>
              </div>

              {/* Row 2: A/B Test + Auto Follow-Up */}
              <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'center' }}>
                {/* A/B Test Toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ExperimentOutlined style={{ color: COLORS.amber, fontSize: 14 }} />
                  <span style={{ fontSize: 12, color: COLORS.textSecondary, fontWeight: 500 }}>A/B Test</span>
                  <Switch
                    checked={step.abTest}
                    onChange={(val) => handleStepChange(index, 'abTest', val)}
                    size="small"
                    style={{ background: step.abTest ? COLORS.amber : undefined }}
                  />
                </div>

                {/* Auto Follow-Up Toggle */}
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <ThunderboltOutlined style={{ color: COLORS.emerald, fontSize: 14 }} />
                  <span style={{ fontSize: 12, color: COLORS.textSecondary, fontWeight: 500 }}>Auto Follow-Up</span>
                  <Switch
                    checked={step.autoFollowUp}
                    onChange={(val) => handleStepChange(index, 'autoFollowUp', val)}
                    size="small"
                    style={{ background: step.autoFollowUp ? COLORS.emerald : undefined }}
                  />
                </div>

                {step.autoFollowUp && (
                  <>
                    <Select
                      value={step.autoFollowUpTrigger}
                      onChange={(val) => handleStepChange(index, 'autoFollowUpTrigger', val)}
                      style={{ minWidth: 160 }}
                      size="small"
                      dropdownStyle={{ background: '#fff' }}
                    >
                      {AUTO_FOLLOWUP_TRIGGERS.map((t) => (
                        <Option key={t.value} value={t.value}>{t.label}</Option>
                      ))}
                    </Select>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                      <span style={{ fontSize: 11, color: COLORS.textMuted }}>after</span>
                      <InputNumber
                        min={1}
                        max={168}
                        value={step.autoFollowUpDelayHours}
                        onChange={(val) => handleStepChange(index, 'autoFollowUpDelayHours', val || 24)}
                        size="small"
                        style={{ width: 60 }}
                      />
                      <span style={{ fontSize: 11, color: COLORS.textMuted }}>hours</span>
                    </div>
                  </>
                )}
              </div>

              {/* A/B Variant Editors */}
              {step.abTest && (
                <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
                  {/* Variant A */}
                  <div style={{
                    flex: 1,
                    ...glassCard,
                    padding: 12,
                    borderTop: `2px solid ${COLORS.blue}`,
                  }}>
                    <div style={{
                      fontSize: 12, fontWeight: 700, color: COLORS.blue, marginBottom: 8,
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 20, height: 20, borderRadius: 5, background: COLORS.blueBg,
                        fontSize: 10, fontWeight: 700,
                      }}>A</span>
                      Variant A
                    </div>
                    <Input
                      placeholder="Subject line for Variant A"
                      value={step.variantA?.subject || ''}
                      onChange={(e) => handleStepChange(index, 'variantA', { ...step.variantA, subject: e.target.value })}
                      size="small"
                      style={{ marginBottom: 8 }}
                    />
                    <TextArea
                      placeholder="Email body for Variant A..."
                      value={step.variantA?.body || ''}
                      onChange={(e) => handleStepChange(index, 'variantA', { ...step.variantA, body: e.target.value })}
                      rows={4}
                      style={{ resize: 'vertical' }}
                    />
                  </div>

                  {/* Variant B */}
                  <div style={{
                    flex: 1,
                    ...glassCard,
                    padding: 12,
                    borderTop: `2px solid ${COLORS.amber}`,
                  }}>
                    <div style={{
                      fontSize: 12, fontWeight: 700, color: COLORS.amber, marginBottom: 8,
                      display: 'flex', alignItems: 'center', gap: 6,
                    }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        width: 20, height: 20, borderRadius: 5, background: COLORS.amberBg,
                        fontSize: 10, fontWeight: 700,
                      }}>B</span>
                      Variant B
                    </div>
                    <Input
                      placeholder="Subject line for Variant B"
                      value={step.variantB?.subject || ''}
                      onChange={(e) => handleStepChange(index, 'variantB', { ...step.variantB, subject: e.target.value })}
                      size="small"
                      style={{ marginBottom: 8 }}
                    />
                    <TextArea
                      placeholder="Email body for Variant B..."
                      value={step.variantB?.body || ''}
                      onChange={(e) => handleStepChange(index, 'variantB', { ...step.variantB, body: e.target.value })}
                      rows={4}
                      style={{ resize: 'vertical' }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Add Step Button */}
      <button
        onClick={handleAddStep}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          padding: '12px 24px',
          borderRadius: 12,
          border: `2px dashed ${COLORS.borderLight}`,
          background: 'transparent',
          color: COLORS.textSecondary,
          fontSize: 13,
          fontWeight: 600,
          cursor: 'pointer',
          transition: 'all 0.2s',
        }}
        onMouseEnter={(e) => {
          e.target.style.borderColor = COLORS.violet;
          e.target.style.color = COLORS.violet;
          e.target.style.background = COLORS.violetBg;
        }}
        onMouseLeave={(e) => {
          e.target.style.borderColor = COLORS.borderLight;
          e.target.style.color = COLORS.textSecondary;
          e.target.style.background = 'transparent';
        }}
      >
        <PlusOutlined />
        Add Step {steps.length + 1}
      </button>
    </div>
  );
};

export default StepBuilder;
